const INTERCOM_API_BASE = "https://api-iam.intercom.io";
const INTERCOM_SESSION_DURATION_MS = 86400000;
const INTERCOM_BOOT_RETRY_MS = 100;
const INTERCOM_BOOT_MAX_ATTEMPTS = 50;

export type IntercomUserSettings = {
  app_id: string;
  intercom_user_jwt: string;
  session_duration?: number;
};

let hasBooted = false;
let bootRetryTimer: ReturnType<typeof setTimeout> | null = null;

const getIntercomPayload = (settings: IntercomUserSettings) => ({
  api_base: INTERCOM_API_BASE,
  app_id: settings.app_id,
  intercom_user_jwt: settings.intercom_user_jwt,
  session_duration: settings.session_duration ?? INTERCOM_SESSION_DURATION_MS,
});

const clearBootRetry = () => {
  if (bootRetryTimer) {
    clearTimeout(bootRetryTimer);
    bootRetryTimer = null;
  }
};

const runBootOrUpdate = (settings: IntercomUserSettings): boolean => {
  const intercom = window.Intercom;
  if (typeof intercom !== "function") {
    return false;
  }

  const payload = getIntercomPayload(settings);

  if (hasBooted) {
    intercom("update", payload);
    return true;
  }

  intercom("boot", payload);
  hasBooted = true;
  return true;
};

const scheduleBootRetry = (settings: IntercomUserSettings, attempt = 0) => {
  clearBootRetry();

  if (attempt >= INTERCOM_BOOT_MAX_ATTEMPTS) {
    return;
  }

  bootRetryTimer = setTimeout(() => {
    if (runBootOrUpdate(settings)) {
      clearBootRetry();
      return;
    }

    scheduleBootRetry(settings, attempt + 1);
  }, INTERCOM_BOOT_RETRY_MS);
};

export const ensureIntercomScript = (appId: string, onReady?: () => void): void => {
  const scriptSelector = `script[src="https://widget.intercom.io/widget/${appId}"]`;
  const existingScript = document.querySelector(scriptSelector);

  if (existingScript) {
    onReady?.();
    return;
  }

  const windowWithIntercom = window as Window & {
    Intercom?: (...args: unknown[]) => void;
  };

  if (typeof windowWithIntercom.Intercom !== "function") {
    const intercomStub = function (...commandArgs: unknown[]) {
      intercomStub.c(commandArgs);
    };
    intercomStub.q = [] as unknown[][];
    intercomStub.c = (commandArgs: unknown[]) => {
      intercomStub.q.push(commandArgs);
    };
    windowWithIntercom.Intercom = intercomStub;
  }

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.src = `https://widget.intercom.io/widget/${appId}`;
  script.onload = () => onReady?.();
  document.body.appendChild(script);
};

export const bootOrUpdateIntercom = (settings: IntercomUserSettings): void => {
  if (runBootOrUpdate(settings)) {
    clearBootRetry();
    return;
  }

  scheduleBootRetry(settings);
};

export const updateIntercom = (): void => {
  if (typeof window.Intercom !== "function" || !hasBooted) {
    return;
  }

  window.Intercom("update");
};

export const shutdownIntercom = (): void => {
  clearBootRetry();

  if (typeof window.Intercom === "function") {
    window.Intercom("shutdown");
  }

  hasBooted = false;
};
